
import {
  defineComponent,
  ref,
  PropType,
  onMounted,
  computed,
  defineExpose,
  reactive,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { hideModal, modalHideListener } from "@/core/helpers/dom";
import { modalShowListener, showModal } from "@/core/directive/function/common";
import { NumberOrString } from "@/core/directive/type/common";
import { TaggingItem } from "@/core/directive/interface/common";
import mixin from "@/mixins";
import _ from "lodash";
import { Currency, KycType } from "@/core/directive/type/kyc";
import { financialFormData } from "@/core/directive/interface/kyc";
import { ApiBase, ApiKyc } from "@/core/api";
import KycAddress from "@/components/page/kyc/common/Address.vue";
import AdditionalSection from "@/components/page/kyc/common/AdditionalSection.vue";

export default defineComponent({
  name: "financial-edit-modal",
  components: {
    KycAddress,
    AdditionalSection,
  },
  props: {
    id: {
      type: Number as PropType<NumberOrString>,
      default: 0,
    },
    updateSection: {
      type: String,
      default: "identification", // identification | additional
    },
    type: {
      type: String as PropType<KycType>,
      default: KycType.Juridical,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update-info"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const localLoading = ref(false);
    const modalRef = ref<null | HTMLElement>(null);
    const formRef = ref<null | HTMLFormElement>(null);
    const {
      showValidateErrorMsg,
      showServerErrorMsg,
      showFormSubmitSuccessMsg,
    } = mixin();

    const taggingLoading = ref(false);
    const finanicalTypeOptions = ref<Array<TaggingItem>>([]);

    const settlementEntityId = route.params.id;
    const isEditModal = computed(() => {
      return props.id !== 0;
    });

    const formData = ref(Object.assign({}, _.cloneDeep(financialFormData)));
    const juridicalRequiredField = {
      holder_registered_company_name: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
      holder_company_legal_form: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
    };
    const naturalRequiredField = {
      holder_name: [
        {
          required: true,
          message: t("common.isRequired"),
          trigger: "change",
        },
      ],
    };
    const rules = reactive({
      identification: {
        financial_type: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        third_party_platform: [
          {
            required: false,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        payment_institution: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        payment_institution_branch: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        account_number: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        bic_swift: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        address1: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        address2: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        postcode: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        city_id: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        country_code: [
          {
            required: true,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        province_id: [
          {
            required: false,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
        district_id: [
          {
            required: false,
            message: t("common.isRequired"),
            trigger: "change",
          },
        ],
      },
    });

    watch(
      () => props.type,
      (newVal) => {
        if (newVal === KycType.Juridical) {
          Object.assign(rules.identification, juridicalRequiredField);
        } else if (newVal === KycType.Natural) {
          Object.assign(rules.identification, naturalRequiredField);
        }
      },
      { immediate: true }
    );

    watch(
      () => formData.value.identification.country_code,
      (newVal) => {
        if (newVal == "CN") {
          rules.identification.province_id = [
            {
              required: true,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
          rules.identification.district_id = [
            {
              required: true,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
        } else if (newVal == "HK" || newVal == "MO") {
          rules.identification.province_id = [
            {
              required: false,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
          rules.identification.district_id = [
            {
              required: false,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
        } else if (newVal == "TW") {
          rules.identification.province_id = [
            {
              required: true,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
          rules.identification.district_id = [
            {
              required: false,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
        }
      }
    );

    watch(
      () => formData.value.identification.financial_type,
      (newVal) => {
        if (newVal == "30" || newVal == "40") {
          rules.identification.third_party_platform = [
            {
              required: true,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
        } else {
          rules.identification.third_party_platform = [
            {
              required: false,
              message: t("common.isRequired"),
              trigger: "change",
            },
          ];
        }
      }
    );

    const getTaggingData = async () => {
      taggingLoading.value = true;
      const { data } = await ApiBase.getTaggingData({
        short_key: ["finanical_account_identification_type"],
      });
      taggingLoading.value = false;
      if (data.code == 0) {
        finanicalTypeOptions.value =
          data.data.finanical_account_identification_type.items;
      }
    };

    const querySearch = async (queryString, cb) => {
      const { data } = await ApiKyc.getCompanyLegalFormData({});
      let suggestions;
      if (data.code == 0) {
        suggestions = data.data;
      }
      const results = queryString
        ? suggestions.filter((s) =>
            s.value.toLowerCase().includes(queryString.toLowerCase())
          )
        : suggestions;
      cb(results);
    };

    /**
     * 请求可编辑数据
     */
    const getEditable = async () => {
      localLoading.value = true;
      const { data } = await ApiKyc.getFinancialEditable({
        id: props.id,
      });
      localLoading.value = false;
      if (data.code === 0) {
        formData.value = data.data;
      } else {
        showServerErrorMsg(data);
      }
    };

    const submit = () => {
      if (!formRef.value) {
        return;
      }
      formRef.value.validate((valid: boolean) => {
        if (valid) {
          if (isEditModal.value) {
            if (props.updateSection === "identification") {
              updateFinancialIdentificationInfo();
            } else {
              updateFinancialAdditionalInfo();
            }
          } else {
            addFinancialInfo();
          }
        } else {
          showValidateErrorMsg();
          return false;
        }
      });
    };

    const addFinancialInfo = async () => {
      const { data } = await ApiKyc.addFinancial({
        settlement_entity_id: settlementEntityId,
        ...formData.value,
      });
      if (data.code === 0) {
        modalHide();
        showFormSubmitSuccessMsg(() => {
          emit("update-info");
        });
      } else {
        showServerErrorMsg(data);
      }
    };

    const updateFinancialIdentificationInfo = async () => {
      localLoading.value = true;
      const { data } = await ApiKyc.updateFinancialIdentification(
        formData.value
      );
      localLoading.value = false;
      if (data.code === 0) {
        modalHide();
        showFormSubmitSuccessMsg(() => {
          emit("update-info");
        });
      } else {
        showServerErrorMsg(data);
      }
    };

    const updateFinancialAdditionalInfo = async () => {
      localLoading.value = true;
      const { data } = await ApiKyc.updateFinancialAdditional(formData.value);
      localLoading.value = false;
      if (data.code === 0) {
        modalHide();
        showFormSubmitSuccessMsg(() => {
          emit("update-info");
        });
      } else {
        showServerErrorMsg(data);
      }
    };

    const modalShow = () => {
      showModal(modalRef.value);
    };

    const modalHide = () => {
      hideModal(modalRef.value);
    };

    const resetFields = () => {
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };

    onMounted(() => {
      getTaggingData();
      modalShowListener(modalRef.value, () => {
        setTimeout(() => {
          if (isEditModal.value) {
            getEditable();
          }
        }, 0);
      });
      modalHideListener(modalRef.value, () => {
        resetFields();
      });
    });

    defineExpose({
      modalShow,
      modalHide,
    });

    return {
      localLoading,
      t,
      modalRef,
      formRef,
      isEditModal,
      formData,
      rules,
      taggingLoading,
      finanicalTypeOptions,
      KycType,
      submit,
      modalShow,
      modalHide,
      querySearch,
    };
  },
});
